<template>
  <validation-provider v-slot="{ errors }" rules="" name="user.combo">
    <component
      :is="inputComponent"
      ref="user"
      :dense="dense"
      v-model="selected"
      v-bind="$attrs"
      @blur="handleBlur"
      @click:clear="internalSearch = null"
      @keydown="handleKeydown"
      :search-input.sync="internalSearch"
      :items="items"
      :item-text="userDisplayName"
      :loading="isLoading"
      :label="label"
      :hint="computedHint"
      :outlined="outlined"
      :multiple="multiple"
      :chips="multiple"
      :deletable-chips="multiple"
      :clearable="!multiple"
      :error-messages="errors"
      return-object
      hide-details="auto"
    >
      <template v-slot:selection="data">
        <v-chip
          v-if="multiple"
          small
          close
          @click:close="remove(data.item)"
          @keydown.delete="handleDeleteKey(data.item)"
          @keydown.backspace="handleDeleteKey(data.item)"
          :ref="`chip-${data.index}`"
          :tabindex="focusedChipIndex === data.index ? 0 : -1"
          :aria-label="`${userDisplayName(data.item)}, naciśnij Delete aby usunąć`"
          class="mt-1 pl-2"
        >
          <v-avatar left v-if="avatar">
            <v-img v-if="data.item.avatar_url" :src="data.item.avatar_url" />
            <v-icon v-else-if="avatar" small>mdi-account</v-icon>
          </v-avatar>
          {{ userDisplayName(data.item) }}
        </v-chip>
        <template v-else>
          {{ userDisplayName(data.item) }}
        </template>
      </template>
      <template slot="item" slot-scope="{ item }">
        <div class="user-combo-item py-1 d-flex align-center">
          <div v-if="avatar" class="user-combo-avatar-slot flex-shrink-0">
            <user-avatar :user="item" small />
          </div>
          <div class="user-combo-content flex-grow-1" :class="{ 'ml-2': avatar }">
            <div class="user-combo-main-line">
              <span class="user-combo-name">{{ userDisplayName(item) }}</span>
              <div v-if="userTags(item).length" class="d-inline-flex align-center flex-wrap">
                <v-chip
                  small outlined label
                  v-for="(tag, index) in userTags(item)"
                  :key="index"
                  :color="tag.color"
                  class="ml-1 my-1"
                >
                  {{ tag.title }}
                </v-chip>
              </div>
            </div>
            <div v-if="workInfo(item)" class="d-flex align-center mt-1 caption text--secondary">
              <v-icon small class="mr-1">mdi-briefcase-outline</v-icon>
              <span class="text-truncate">{{ workInfo(item) }}</span>
            </div>
          </div>
        </div>
      </template>
    </component>
  </validation-provider>
</template>

<script>
import { userWorkInfo } from "@/filters/user"
import { VCombobox, VAutocomplete } from 'vuetify/lib'
import UserAvatar from '@/components/UserAvatar.vue'

export default {
  name: "UsersCombo",
  components: { VCombobox, VAutocomplete, UserAvatar },

  beforeMount() {
    this.selected = this.normalizeSelection(this.usersSelected)
    this.internalSearch = this.searchInput
    this.loadUsers(this.users, this.selected, this.internalSearch)
  },
  data: () => ({
    selected: null,
    isLoading: false,
    syncingFromProp: false,
    internalSearch: null,
    items: [],
    focusedChipIndex: -1,
    closeAfterSelect: false,
  }),
  props: {
    label : {
      type: String,
      default: '',
    },
    hint : {
      type: String,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    autocommit: {
      type: Boolean,
      default: false,
    },
    searchInput: {
      default: undefined
    },
    outlined: {
      type: Boolean,
      default: true,
    },
    dense: {
      type: Boolean,
      default: true,
    },
    users: {
      type: [Promise, Function],
      default: () => new Promise((resolve) => {
        resolve([])
      })
    },
    usersSelected: {
      type: [Object, Array, String, Number],
      default: null,
    },
    allowNew: {
      type: Boolean,
      default: true,
    },
    avatar: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    internalSearch(value) {
      this.$emit('update:searchInput', value)

      if (typeof this.users === "function") {
        this.loadUsers(this.users, this.selected, value)
      }
    },
    searchInput(value) {
      if (value !== this.internalSearch) {
        this.internalSearch = value
      }
    },
    users(value) {
      this.loadUsers(value, this.selected, this.internalSearch)
    },
    usersSelected(value) {
      const normalized = this.normalizeSelection(value)
      const changed = !this.isSameSelection(this.selected, normalized)

      this.syncingFromProp = changed
      if (changed) {
        this.selected = normalized
      }
    },
    selected(value) {
      const normalized = this.normalizeSelection(value)

      if (this.syncingFromProp) {
        this.syncingFromProp = false
        this.ensureSelectedInItems(normalized)
        return
      }

      if (this.needsSelectionNormalization(value)) {
        this.selected = normalized
        if (!this.allowNew) {
          this.$nextTick(() => { this.internalSearch = null })
        }
        return
      }

      this.ensureSelectedInItems(normalized)
      this.$emit("user-selected", normalized)

      // Handle close after Enter in multiselect
      if (this.closeAfterSelect && this.multiple) {
        this.closeAfterSelect = false
        // Use setTimeout to ensure selection has been processed by V-combobox
        setTimeout(() => {
          // Close the dropdown
          if (this.$refs.user?.isMenuActive) {
            this.$refs.user.isMenuActive = false
          }
          // Clear the search field
          this.internalSearch = null

          // Defer focus move to ensure DOM settled
          this.$nextTick(() => {
            // Move focus to next focusable element (natural Tab flow)
            const focusableElements = document.querySelectorAll(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
            const currentElement = this.$refs.user?.$el
            let foundCurrent = false

            for (const el of focusableElements) {
              if (foundCurrent && el.offsetParent !== null) { // offsetParent check = is visible
                el.focus()
                break
              }
              if (el === currentElement || el.contains(currentElement)) {
                foundCurrent = true
              }
            }
          })
        }, 100)
      }
    }
  },
  computed: {
    inputComponent() {
      return this.allowNew ? VCombobox : VAutocomplete
    },
    computedHint() {
      if (!this.multiple) {
        return this.hint
      }

      const keyboardHint = "Enter = zaznacz i koniec | Ctrl+Enter = wielokrotnie | Delete = usuń"
      if (!this.hint) {
        return keyboardHint
      }

      return `${this.hint} — ${keyboardHint}`
    }
  },
  methods: {
    loadUsers(usersSource, selection = this.selected, searchValue = this.internalSearch) {
      let usersPromise = usersSource

      if (typeof usersSource === "function") {
        usersPromise = usersSource(searchValue)
      }

      if (!usersPromise || typeof usersPromise.then !== "function") {
        this.items = []
        this.ensureSelectedInItems(selection)
        return
      }

      this.isLoading = true
      usersPromise
        .then(data => {
          // Sort users by display name (what user sees)
          this.items = this.sortUsers(data) || []
        })
        .catch(() => {
          this.items = []
        })
        .finally(() => {
          this.ensureSelectedInItems(selection)
          this.isLoading = false
        })
    },
    handleBlur() {
      if (this.allowNew || this.multiple) {
        return
      }

      const normalized = this.normalizeSelection(this.selected)
      if (this.needsSelectionNormalization(this.selected)) {
        this.selected = normalized
      }

      this.$nextTick(() => {
        // In single mode selected value is rendered by selection slot,
        // so keeping search text causes duplicated name in the input.
        this.internalSearch = null
      })
    },
    workInfo(item) {
      return userWorkInfo(item)
    },
    userTags(item) {
      if (!item || !Array.isArray(item.tags)) {
        return []
      }

      return item.tags
    },
    userDisplayName(item) {
      if (!item) {
        return ""
      }
      if (typeof item === "string") {
        return item
      }
      if (typeof item === "number") {
        return item.toString()
      }

      const byFilter = this.$options.filters.userName(item)
      return item._displayName || byFilter || item.text || item.value || ""
    },
    normalizeUser(user) {
      if (!user) {
        return null
      }

      if (typeof user === "string") {
        if (!this.allowNew) {
          return null
        }
        return {
          id: 0,
          full_name: user,
          _displayName: user,
        }
      }

      if (typeof user === "number") {
        return {
          id: user,
          full_name: user.toString(),
          _displayName: user.toString(),
        }
      }

      if (!this.allowNew && this.isDisallowedObjectValue(user)) {
        return null
      }

      return user
    },
    normalizeSelection(value) {
      if (value == null) {
        return this.multiple ? [] : null
      }

      if (this.multiple) {
        if (Array.isArray(value)) {
          return value.map(this.normalizeUser).filter(Boolean)
        }
        const normalized = this.normalizeUser(value)
        return normalized ? [normalized] : []
      }

      if (Array.isArray(value)) {
        return this.normalizeUser(value[0])
      }

      return this.normalizeUser(value)
    },
    needsSelectionNormalization(value) {
      if (value == null) {
        return false
      }

      if (this.multiple) {
        if (!Array.isArray(value)) {
          return true
        }

        return value.some(item => this.isDisallowedSelectionValue(item))
      }

      if (Array.isArray(value)) {
        return true
      }

      return this.isDisallowedSelectionValue(value)
    },
    isDisallowedSelectionValue(value) {
      if (typeof value === "string" || typeof value === "number") {
        return true
      }

      if (!this.allowNew && this.isDisallowedObjectValue(value)) {
        return true
      }

      return false
    },
    isDisallowedObjectValue(value) {
      if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false
      }

      if (value.id != null && value.id !== 0) {
        return false
      }

      const hasKnownIdentity = Boolean(
        value.full_name ||
        value.first_name ||
        value.last_name ||
        value.username ||
        value.email ||
        value.name
      )
      if (hasKnownIdentity) {
        return false
      }

      const displayName = this.userDisplayName(value)
      if (!displayName) {
        return true
      }

      const hasExistingMatch = this.items.some(item => this.isSameUser(item, value))
      return !hasExistingMatch
    },
    isSameUser(left, right) {
      if (!left || !right) {
        return false
      }

      if (left.id != null && right.id != null && left.id !== 0 && right.id !== 0) {
        return left.id === right.id
      }

      return this.userDisplayName(left).toLowerCase() === this.userDisplayName(right).toLowerCase()
    },
    isSameSelection(left, right) {
      if (left == null && right == null) {
        return true
      }

      if (!left || !right) {
        return false
      }

      if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right)) {
          return false
        }
        if (left.length !== right.length) {
          return false
        }

        return left.every((item, index) => this.isSameUser(item, right[index]))
      }

      return this.isSameUser(left, right)
    },
    ensureSelectedInItems(selection) {
      if (!selection) {
        return
      }

      const selectedItems = Array.isArray(selection) ? selection : [selection]
      for (const selectedItem of selectedItems) {
        if (!selectedItem) {
          continue
        }

        const exists = this.items.some(item => this.isSameUser(item, selectedItem))
        if (!exists) {
          this.items.push(selectedItem)
        }
      }
    },
    sortUsers(users) {
      if (!users || !Array.isArray(users)) return users

      return users
        .slice()
        .map(user => this.normalizeUser(user))
        .filter(Boolean)
        .sort((a, b) => {
          return this.userDisplayName(a)
            .toLowerCase()
            .localeCompare(this.userDisplayName(b).toLowerCase(), "pl")
        })
    },
    focus() {
      this.$refs.user.focus()
    },
    remove(item) {
      if (Array.isArray(this.selected)) {
        const index = this.selected.findIndex(selectedItem => this.isSameUser(selectedItem, item))
        if (index >= 0) this.selected.splice(index, 1)
        return
      }

      if (this.isSameUser(this.selected, item)) {
        this.selected = null
      }
    },
    handleKeydown(event) {
      // Only handle in multiselect mode
      if (!this.multiple) {
        return
      }

      if (event.key === 'Enter') {
        if (event.ctrlKey || event.metaKey) {
          // Ctrl+Enter: zaznacz bez zamykania dropdown
          // Don't prevent default - let V-combobox select the item
          // Just clear search for next entry
          this.$nextTick(() => {
            this.internalSearch = null
          })
        } else {
          // Enter: zaznacz i zamknij dropdown
          // Set flag to close after selection completes
          this.closeAfterSelect = true
          // Don't prevent default - let V-combobox select the item
        }
      }
    },
    handleEnter() {
      // This is now handled in the selected() watcher
      // Kept for backward compatibility if needed
    },
    handleDeleteKey(item) {
      this.remove(item)
    },
    focusChip(index) {
      this.focusedChipIndex = index
      this.$nextTick(() => {
        const chipRef = this.$refs[`chip-${index}`]
        if (chipRef && Array.isArray(chipRef)) {
          chipRef[0]?.focus()
        } else if (chipRef) {
          chipRef.focus()
        }
      })
    },
  },
}
</script>

<style lang="sass" scoped>
.user-combo-item
  line-height: 1.25

.user-combo-avatar-slot
  width: 62px
  min-width: 62px
  display: flex
  align-items: center
  justify-content: center

.user-combo-content
  min-width: 0

.user-combo-main-line
  display: flex
  align-items: center
  flex-wrap: wrap
  gap: 4px

.user-combo-name
  font-size: 0.95rem
  font-weight: 500
  letter-spacing: 0.01em


</style>